/**
 * Dinero en pesos colombianos, siempre entero.
 *
 * Es un tipo marcado a propósito: en JavaScript plano un monto y una cantidad de
 * billetes son ambos `number`, así que multiplicar el par equivocado produce un
 * número plausible sin ningún error. Con la marca, ese código no compila.
 */
export type COP = number & { readonly __marca: 'COP' };

/** Cantidad de unidades físicas, por ejemplo billetes de una denominación. */
export type Cantidad = number & { readonly __marca: 'Cantidad' };

export function cop(n: number): COP {
  if (!Number.isSafeInteger(n)) {
    throw new TypeError(`COP debe ser un entero seguro, recibió ${String(n)}`);
  }
  // `n + 0` normaliza -0 a 0. Un peso negativo cero no significa nada, pero JavaScript
  // lo distingue: `Object.is(-0, 0)` es falso, así que dos importes que deberían ser el
  // mismo comparan distinto. Lo encontró la propiedad de simetría de la adjudicación 29,
  // y sin esto se filtra hasta la interfaz como un importe formateado "-0".
  return (n + 0) as COP;
}

export function cantidad(n: number): Cantidad {
  if (!Number.isSafeInteger(n) || n < 0) {
    throw new TypeError(`Cantidad debe ser un entero no negativo, recibió ${String(n)}`);
  }
  return n as Cantidad;
}

export function sumaCOP(...valores: readonly COP[]): COP {
  let total = 0;
  for (const v of valores) total += v;
  return cop(total);
}

export function restaCOP(a: COP, b: COP): COP {
  return cop(a - b);
}

/**
 * Porcentaje sobre una base, redondeado a la **mitad lejos del cero**.
 *
 * Se usa una sola regla de redondeo en todo el sistema, definida acá, porque la
 * feature 3 no deja cerrar la jornada hasta que el desglose cuadre exacto: un
 * peso de diferencia por redondeo deja al operario atrapado a las tres de la
 * mañana sin entender por qué.
 *
 * Lejos del cero y no hacia arriba, que para bases negativas no es lo mismo:
 * `porcentajeCOP(cop(-101), 50)` da -51 y no -50. La razón es la simetría,
 * `porcentajeCOP(-x, p) === -porcentajeCOP(x, p)`, sin la cual revertir un
 * movimiento deja un peso colgando — justo el descuadre que esto evita.
 * Adjudicación 29 del plan maestro.
 */
export function porcentajeCOP(base: COP, porcentaje: number): COP {
  if (!Number.isFinite(porcentaje)) {
    throw new TypeError(`El porcentaje debe ser finito, recibió ${String(porcentaje)}`);
  }
  const signo = base < 0 ? -1 : 1;
  const producto = Math.abs(base) * porcentaje;
  // El producto intermedio puede pasarse del entero seguro aunque la base y el
  // resultado quepan de sobra, y ahí el redondeo se hace sobre un float ya corrompido:
  // devuelve un peso de más sin lanzar nada. Un número plausible y equivocado es peor
  // que una excepción, porque nadie lo nota. Falla en voz alta y nombra el producto.
  //
  // Se mira la MAGNITUD y no si es entero: con un porcentaje fraccionario el producto
  // legítimamente no lo es (2,5% de 1.001 son 2.502,5), y exigirle ser entero rompía
  // todo cálculo con tarifas como el ReteICA del 0,6%. Lo dijo la propiedad de simetría.
  if (Math.abs(producto) > Number.MAX_SAFE_INTEGER) {
    throw new RangeError(
      `El porcentaje ${String(porcentaje)} sobre ${String(base)} excede la precisión entera de JavaScript en el paso intermedio (${String(producto)})`,
    );
  }
  const bruto = producto / 100;
  return cop(signo * Math.round(bruto));
}

/**
 * Divide un total en partes iguales y redondea con **la misma regla que `porcentajeCOP`**:
 * mitad lejos del cero.
 *
 * ESTÁ ACÁ Y NO EN `reglas/inventario.ts`, que es quien la necesitó primero, porque la
 * adjudicación 29 dice que hay una sola regla de redondeo y que ninguna otra función define la
 * suya. El bloque de la tarea 1 de la fase 8 traía `Math.floor(x + 0.5)` escrito adentro del
 * costo promedio, con el comentario «la única regla del proyecto», y eso es medio hacia
 * ARRIBA, que es justo lo que la adjudicación 29 corrigió: para bases positivas coincide y
 * para negativas no. Una segunda regla de redondeo, aunque hoy dé el mismo número, es la que
 * alguien copia mañana a un sitio donde sí importa.
 *
 * Lejos del cero por la simetría: `dividirCOP(-x, n) === -dividirCOP(x, n)`, sin la cual
 * revertir un movimiento deja un peso colgando.
 *
 * Dividir por cero devuelve cero y no lanza: el costo promedio de un ítem sin entradas es
 * cero, no un error, y quien pregunta por el promedio de algo que nunca se compró merece una
 * respuesta y no una excepción.
 */
export function dividirCOP(total: COP, partes: Cantidad): COP {
  if (partes === 0) return cop(0);
  const signo = total < 0 ? -1 : 1;
  return cop(signo * Math.round(Math.abs(total) / partes));
}

/**
 * Convierte a COP un valor que viene de la API de datos.
 *
 * Las columnas bigint de Postgres llegan como CADENA en el JSON. Sin este parseo
 * explícito, "1500000" + "300000" da "1500000300000" sin ningún error, y ese es el
 * bug más silencioso del stack. Todo monto que cruza el borde de la red pasa por acá.
 */
export function parseCOPdeAPI(v: string | number | null | undefined): COP {
  if (v === null || v === undefined) return cop(0);
  if (typeof v === 'number') return cop(v);
  if (!/^-?\d+$/.test(v)) {
    throw new TypeError(`El valor recibido de la API no es un entero: ${JSON.stringify(v)}`);
  }
  const n = Number(v);
  // Un bigint de Postgres puede exceder el entero seguro de JavaScript, y ahí Number()
  // ya perdió precisión. `cop()` lo rechaza igual, pero informando el número corrompido:
  // diría 9007199254740992 cuando la API mandó ...993, y quien lo busque en sus datos no
  // lo va a encontrar. Se compara la ida y vuelta para poder nombrar el valor original.
  if (!Number.isSafeInteger(n) || String(n) !== v.replace(/^-0$/, '0')) {
    throw new TypeError(
      `El valor recibido de la API excede el entero seguro de JavaScript: ${JSON.stringify(v)}`,
    );
  }
  return cop(n);
}

/**
 * Reparte un total en partes iguales y asigna el residuo al ÚLTIMO beneficiario.
 *
 * La asignación es determinista a propósito, y la interfaz debe mostrar a quién le
 * tocó el residuo: si se esconde, alguien va a creer que es un error del sistema.
 * Repartir 600.000 entre 7 da seis de 85.714 y uno de 85.716.
 */
export function repartoConResiduo(total: COP, partes: number): COP[] {
  if (!Number.isSafeInteger(partes) || partes < 1) {
    throw new RangeError(`El reparto necesita al menos una parte, recibió ${String(partes)}`);
  }
  const base = Math.trunc(total / partes);
  const residuo = total - base * partes;
  const resultado: COP[] = [];
  for (let i = 0; i < partes; i++) {
    resultado.push(cop(i === partes - 1 ? base + residuo : base));
  }
  return resultado;
}
